import { useState } from "react";
import { useConvexEnableSync } from "@/hooks/useConvexEnableSync";
import { getJoinUrl } from "@/helpers/joinUrl";

export const useShareGame = (slug: string | null) => {
  const { enableSync, reshare } = useConvexEnableSync();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const share = async (start: typeof enableSync) => {
    setIsLoading(true);
    setError(null);

    try {
      const syncedGame = await start();
      if (!syncedGame?.slug) {
        setError("Couldn't share this game.");
      }
    } catch (err) {
      setError("Couldn't share this game.");
      console.error("Failed to enable sync:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableSync = () => share(enableSync);
  const handleReshare = () => share(reshare);

  const handleShareUrl = async () => {
    if (!slug) return;

    const url = getJoinUrl(slug);

    try {
      // Try native share API first (better for mobile)
      if (navigator.share) {
        await navigator.share({
          title: "Court Shuffle Game",
          url,
        });
        return;
      }

      // Fallback to clipboard for desktop
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to share/copy:", err);

      // Final fallback - copy just the URL
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (copyErr) {
        console.error("Copy also failed:", copyErr);
      }
    }
  };

  const resetShareState = () => {
    setCopied(false);
    setError(null);
  };

  return {
    isLoading,
    error,
    copied,
    handleEnableSync,
    handleReshare,
    handleShareUrl,
    resetShareState,
  };
};
